package com.evoapps.evoshift.viewmodels

import androidx.compose.runtime.Stable
import com.evoapps.evoshift.domain.model.Availability
import com.evoapps.evoshift.domain.model.BusyElsewhere
import com.evoapps.evoshift.domain.model.DayStaffAlert
import com.evoapps.evoshift.domain.model.DayStaffing
import com.evoapps.evoshift.domain.model.Roster
import com.evoapps.evoshift.domain.model.Shift
import com.evoapps.evoshift.domain.model.ShiftLeadRule
import com.evoapps.evoshift.domain.model.StaffReadiness
import com.evoapps.evoshift.domain.model.User
import com.evoapps.evoshift.domain.budget.LabourBudgetRules
import com.evoapps.evoshift.domain.budget.LabourBudgetStatus
import com.evoapps.evoshift.domain.schedule.BusyElsewhereCheck
import com.evoapps.evoshift.domain.schedule.ElsewhereIssue
import com.evoapps.evoshift.domain.schedule.LabourCodeCheck
import com.evoapps.evoshift.domain.schedule.LabourProfile
import com.evoapps.evoshift.domain.schedule.LabourReport
import com.evoapps.evoshift.domain.schedule.RosterIssue
import com.evoapps.evoshift.domain.schedule.RosterIssueReport
import com.evoapps.evoshift.domain.schedule.RosterIssues
import com.evoapps.evoshift.domain.usecase.finance.RosterBudgetContext
import com.evoapps.evoshift.domain.util.calculateDurationHours
import kotlinx.datetime.LocalDate

/**
 * SH-158: what the roster grid works out from the month (warnings, the labour code, hours per
 * person, each person's shifts per day, the budget), kept apart from the screen's state so a copy
 * of the state that changes only a dialog, a notice or the selected day keeps the same results
 * (and the same instances: the grid's cells skip recomposing) instead of working them out again.
 *
 * Each result is worked out on first read and only from its own inputs; a holder made for new
 * inputs ([previous] given) takes over every result whose inputs are the same objects, so a new
 * budget does not recompute the warnings, nor a new shift list the people's names. Inputs are
 * compared by reference: the view model replaces a map or list whenever its content changes.
 */
@Stable
class RosterDerived(
    private val shiftsByDate: Map<LocalDate, List<Shift>>,
    private val roster: Roster?,
    private val availableEmployees: List<User>,
    private val labourProfiles: Map<String, LabourProfile>,
    private val nominalMonthlyHours: Double,
    private val restRulesForAllContracts: Boolean,
    private val availabilityByUserId: Map<String, Availability>,
    private val readinessByUserId: Map<String, StaffReadiness>,
    private val busyElsewhere: List<BusyElsewhere>,
    private val leadRule: ShiftLeadRule,
    private val budget: RosterBudgetContext?,
    private val firstDayOfWeek: Int,
    previous: RosterDerived? = null
) {
    /** Whether this holder was made from exactly these inputs (by reference; numbers by value). */
    fun matches(
        shiftsByDate: Map<LocalDate, List<Shift>>,
        roster: Roster?,
        availableEmployees: List<User>,
        labourProfiles: Map<String, LabourProfile>,
        nominalMonthlyHours: Double,
        restRulesForAllContracts: Boolean,
        availabilityByUserId: Map<String, Availability>,
        readinessByUserId: Map<String, StaffReadiness>,
        busyElsewhere: List<BusyElsewhere>,
        leadRule: ShiftLeadRule,
        budget: RosterBudgetContext?,
        firstDayOfWeek: Int
    ): Boolean =
        this.shiftsByDate === shiftsByDate && this.roster === roster && this.availableEmployees === availableEmployees &&
            this.labourProfiles === labourProfiles && this.nominalMonthlyHours == nominalMonthlyHours &&
            this.restRulesForAllContracts == restRulesForAllContracts && this.availabilityByUserId === availabilityByUserId &&
            this.readinessByUserId === readinessByUserId && this.busyElsewhere === busyElsewhere &&
            this.leadRule === leadRule && this.budget === budget && this.firstDayOfWeek == firstDayOfWeek

    private val allShiftsMemo: Memo<List<Shift>> = memo(previous?.allShiftsMemo, shiftsByDate) { shiftsByDate.values.flatten() }
    private val allShifts: List<Shift> get() = allShiftsMemo.value

    private val busyByUserMemo: Memo<Map<String, Map<LocalDate, List<BusyElsewhere>>>> = memo(previous?.busyByUserMemo, busyElsewhere) {
        busyElsewhere.groupBy { it.userId }.mapValues { (_, busy) -> busy.groupBy { LocalDate.parse(it.date) } }
    }
    val busyElsewhereByUserAndDate: Map<String, Map<LocalDate, List<BusyElsewhere>>> get() = busyByUserMemo.value

    private val elsewhereMemo: Memo<Map<String, ElsewhereIssue>> = memo(previous?.elsewhereMemo, shiftsByDate, busyElsewhere) {
        BusyElsewhereCheck.check(allShifts, busyElsewhere)
    }
    val elsewhereIssues: Map<String, ElsewhereIssue> get() = elsewhereMemo.value

    private val budgetMemo: Memo<LabourBudgetStatus?> = memo(previous?.budgetMemo, budget, roster, firstDayOfWeek, shiftsByDate) {
        val context = budget ?: return@memo null
        val roster = roster ?: return@memo null
        // SH-210: paid interviews cost like their participants' shifts.
        LabourBudgetRules.status(context.budget, roster.year, roster.month, firstDayOfWeek, allShifts + context.interviewShifts, context.estimate)
    }
    val budgetStatus: LabourBudgetStatus? get() = budgetMemo.value

    private val dayWarningsMemo: Memo<Map<LocalDate, DayStaffAlert>> = memo(previous?.dayWarningsMemo, shiftsByDate, roster, availableEmployees, leadRule) {
        val workplaceId = roster?.workplaceId ?: return@memo emptyMap()
        val engagements = availableEmployees.associate { it.uid to it.engagements[workplaceId] }
        DayStaffing.warnings(allShifts, engagements, leadRule).mapKeys { (date, _) -> LocalDate.parse(date) }
    }
    val dayWarnings: Map<LocalDate, DayStaffAlert> get() = dayWarningsMemo.value

    private val unsupervisedMemo: Memo<Set<String>> = memo(previous?.unsupervisedMemo, shiftsByDate, roster, availableEmployees, leadRule) {
        dayWarnings.values.flatMapTo(HashSet()) { it.shiftIds }
    }
    val unsupervisedShiftIds: Set<String> get() = unsupervisedMemo.value

    private val labourMemo: Memo<LabourReport> = memo(
        previous?.labourMemo, shiftsByDate, roster, labourProfiles, nominalMonthlyHours, restRulesForAllContracts
    ) {
        val roster = roster ?: return@memo LabourReport.EMPTY
        LabourCodeCheck.check(allShifts, roster.year, roster.month, labourProfiles, nominalMonthlyHours, restRulesForAllContracts)
    }
    val labourReport: LabourReport get() = labourMemo.value

    private val labourIdsMemo: Memo<Set<String>> = memo(
        previous?.labourIdsMemo, shiftsByDate, roster, labourProfiles, nominalMonthlyHours, restRulesForAllContracts
    ) { labourReport.byShiftId.keys }
    val labourWarningShiftIds: Set<String> get() = labourIdsMemo.value

    private val issueInputs: Array<Any?> = arrayOf(
        shiftsByDate, roster, labourProfiles, nominalMonthlyHours, restRulesForAllContracts, availableEmployees,
        leadRule, availabilityByUserId, readinessByUserId, busyElsewhere
    )

    private val issuesMemo: Memo<RosterIssueReport> = memo(previous?.issuesMemo, *issueInputs) {
        val roster = roster ?: return@memo RosterIssueReport.EMPTY
        val workplaceId = roster.workplaceId
        val prefix = "${roster.year}-${roster.month.toString().padStart(2, '0')}-"
        RosterIssues.find(
            shifts = allShifts,
            year = roster.year,
            month = roster.month,
            profiles = labourProfiles,
            nominalMonthlyHours = nominalMonthlyHours,
            restForAllContracts = restRulesForAllContracts,
            engagements = availableEmployees.associate { it.uid to it.engagements[workplaceId] },
            leadRule = leadRule,
            availability = availabilityByUserId.mapValues { (_, a) ->
                a.days.entries.associate { (day, pref) -> "$prefix${day.toString().padStart(2, '0')}" to pref.status }
            },
            expiredChecks = { uid, date -> readinessByUserId[uid]?.expiredOn(date).orEmpty() },
            // SH-136: work elsewhere shows in the shift and person popovers too.
            busyElsewhere = busyElsewhere
        )
    }
    val issues: RosterIssueReport get() = issuesMemo.value

    private val issuesByUserMemo: Memo<Map<String, List<RosterIssue>>> = memo(previous?.issuesByUserMemo, *issueInputs) {
        val owner = allShifts.associate { it.id to it.userId }
        val supervision = issues.issues.filterIsInstance<RosterIssue.OnlyNeedsSupervision>()
        val users = issues.byUserId.keys + supervision.flatMap { it.shiftIds.mapNotNull { id -> owner[id] } }
        users.associateWith { uid ->
            issues.byUserId[uid].orEmpty() + supervision.filter { s -> s.shiftIds.any { owner[it] == uid } }
        }
    }
    val issuesByUserId: Map<String, List<RosterIssue>> get() = issuesByUserMemo.value

    private val plannedHoursMemo: Memo<Map<String, Double>> = memo(previous?.plannedHoursMemo, shiftsByDate) {
        allShifts.filter { it.userId != null }
            .groupBy { it.userId!! }
            .mapValues { (_, shifts) -> shifts.sumOf { calculateDurationHours(it.plannedTime.start, it.plannedTime.end) } }
    }
    val plannedHoursByUserId: Map<String, Double> get() = plannedHoursMemo.value

    private val namesMemo: Memo<Map<String, String>> = memo(previous?.namesMemo, availableEmployees) { availableEmployees.associate { it.uid to it.displayName } }
    val personNames: Map<String, String> get() = namesMemo.value

    private val byUserAndDateMemo: Memo<Map<String, Map<LocalDate, List<Shift>>>> = memo(previous?.byUserAndDateMemo, shiftsByDate) {
        val result = LinkedHashMap<String, LinkedHashMap<LocalDate, MutableList<Shift>>>()
        shiftsByDate.forEach { (date, shifts) ->
            shifts.forEach { shift ->
                val uid = shift.userId ?: return@forEach
                result.getOrPut(uid) { LinkedHashMap() }.getOrPut(date) { ArrayList(1) }.add(shift)
            }
        }
        result
    }
    val shiftsByUserAndDate: Map<String, Map<LocalDate, List<Shift>>> get() = byUserAndDateMemo.value

    /** Holders made from the same inputs are interchangeable (the state's equality ignores which one it carries). */
    override fun equals(other: Any?): Boolean = other is RosterDerived && (other === this || other.matches(
        shiftsByDate, roster, availableEmployees, labourProfiles, nominalMonthlyHours, restRulesForAllContracts,
        availabilityByUserId, readinessByUserId, busyElsewhere, leadRule, budget, firstDayOfWeek
    ))

    override fun hashCode(): Int = 0

    override fun toString(): String = "RosterDerived"

    /** One result, worked out on first read, with the inputs it was worked out from. */
    private class Memo<T>(private val keys: Array<out Any?>, compute: () -> T) {
        val value: T by lazy(compute)

        fun sameKeys(other: Array<out Any?>): Boolean =
            keys.size == other.size && keys.indices.all { i -> sameInput(keys[i], other[i]) }
    }

    private companion object {
        /** By reference, except numbers and flags (boxed anew on every read). */
        fun sameInput(a: Any?, b: Any?): Boolean = a === b || ((a is Number || a is Boolean) && a == b)

        fun <T> memo(previous: Memo<T>?, vararg keys: Any?, compute: () -> T): Memo<T> =
            previous?.takeIf { it.sameKeys(keys) } ?: Memo(keys, compute)
    }
}
